
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../auth/authConfig';
import { markExplicitlySignedOut } from '../../auth/logoutState';
import styles from './LoginButton.module.css';

/**
 * LoginButton — shows a grey initials avatar in the navbar.
 * Clicking it opens a dropdown with the user's name and a Logout option.
 *
 * Accessibility:
 * - Avatar button has aria-expanded and aria-haspopup for screen readers.
 * - Dropdown is closed on outside click and on Escape key.
 * - All interactive elements have visible focus indicators.
 *
 * Security:
 * - Uses loginRedirect (Auth Code + PKCE). Tokens are never exposed in the UI.
 */
export default function LoginButton() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  /** Derive initials (up to 2 chars) from display name or username. */
  const initials = React.useMemo(() => {
    const name = account?.name || account?.username || '';
    const parts = name.split(/[\s@]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }, [account]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const handleLogout = useCallback(() => {
    setOpen(false);
    markExplicitlySignedOut();
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
      account: instance.getActiveAccount(),
    }).catch((err) => {
      if (process.env.NODE_ENV !== 'production') console.error('[Auth] logoutRedirect error:', err);
    });
  }, [instance]);

  // Show skeleton while MSAL is processing a redirect
  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return <div className={styles.skeleton} role="status" aria-busy="true" aria-label="Authenticating…" />;
  }

  if (isAuthenticated && account) {
    const displayName = account.name || account.username || 'User';
    return (
      <div className={styles.wrapper} ref={wrapperRef}>
        {/* Avatar trigger */}
        <button
          type="button"
          className={styles.avatarBtn}
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={`Account menu for ${displayName}`}
        >
          {initials}
        </button>

        {/* Dropdown */}
        {open && (
          <div className={styles.dropdown} role="menu" aria-label="Account menu">
            {/* User info row */}
            <div className={styles.userRow}>
              <div className={styles.dropdownAvatar} aria-hidden="true">{initials}</div>
              <span className={styles.displayName}>{displayName}</span>
            </div>

            <div className={styles.divider} role="separator" />

            {/* Logout */}
            <button
              type="button"
              className={styles.logoutItem}
              onClick={handleLogout}
              role="menuitem"
              aria-label="Sign out"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    );
  }

  // Unauthenticated — button not shown (ProtectedRoute handles auto-redirect)
  return null;
}
